import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import purchaseOrderService from '../../services/purchaseOrderService';
import validateUtils from '../../utils/validate';
import { todayIST } from '../../utils/dates';

const sanitizeNumber = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v).replace(/[^0-9.]/g, '');
    return s;
};

const PurchaseOrderAdviceForm = ({ isOpen, onClose, onSubmit, orderId }) => {
    console.log('PurchaseOrderAdviceForm Render - isOpen:', isOpen, 'orderId:', orderId);

    const { t } = useTranslation();
    const formStrings = t('purchaseOrdersModule.purchaseOrders.forms.adviceForm', { returnObjects: true });
    const commonStrings = t('purchaseOrdersModule.common', { returnObjects: true });

    const today = todayIST();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        advice_date: today,
        payment_terms: '',
        vehicle_number: '',
        supplier_invoice_number: '',
        supplier_invoice_date: today,
        remark: '',
        items_update: [],
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    // True when the by-id PO fetch fails/404s — show an error instead of a blank
    // form the operator could submit as a zero-item advice.
    const [fetchError, setFetchError] = useState(false);
    const [showVehicleTooltip, setShowVehicleTooltip] = useState(false);
    const fetchedIdRef = useRef(null);

    useEffect(() => {
        if (isOpen && orderId && fetchedIdRef.current !== orderId) {
            fetchedIdRef.current = orderId;
            console.log('Advice Form: Fetching full order details for ID:', orderId);
            const fetchOrder = async () => {
                setLoading(true);
                setFetchError(false);
                try {
                    const result = await purchaseOrderService.getPurchaseOrderById(orderId, { force: true });
                    if (result.success && result.data) {
                        const order = result.data;
                        console.log('Advice Form: Order details fetched successfully:', order.po_number || orderId);

                        const mappedItems = (order.items || []).map(item => {
                            // Calculate actual pending quantity by subtracting total advised from ordered
                            const orderedQty = parseFloat(item.quantity) || 0;
                            const totalAdvised = (item.advices || []).reduce((sum, advice) => {
                                return sum + (parseFloat(advice.quantity_in_advice) || 0);
                            }, 0);
                            const actualPending = orderedQty - totalAdvised;

                            return {
                                id: item.id,
                                product_size_id: item.product_size_id,
                                product_name: item.size_name || item.product_size?.alias_name || item.product_size?.product_name || 'Unknown Item',
                                ordered_quantity: item.quantity,
                                pending_quantity: actualPending > 0 ? actualPending : 0,
                                quantity: actualPending > 0 ? actualPending : 0, // Default to actual pending quantity
                                unit: item.unit,
                                price: item.price,
                                remark: item.remark || '',
                            };
                        });

                        console.log('Mapped items:', mappedItems);

                        setFormData({
                            advice_date: today,
                            payment_terms: String(order.payment_terms ?? 10),
                            vehicle_number: '',
                            supplier_invoice_number: '',
                            supplier_invoice_date: today,
                            remark: order.remark || '',
                            items_update: mappedItems,
                        });
                        setErrors({});
                    } else {
                        console.warn('API call successful but returned no data or success=false', result);
                        setFetchError(true);
                        fetchedIdRef.current = null;  // allow a retry on reopen
                    }
                } catch (error) {
                    console.error('Failed to fetch order details', error);
                    setFetchError(true);
                    fetchedIdRef.current = null;
                } finally {
                    setLoading(false);
                }
            };
            fetchOrder();
        }
    }, [isOpen, orderId, today]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleItemChange = (index, field, value) => {
        setFormData(prev => {
            const items = [...prev.items_update];
            items[index] = { ...items[index], [field]: (field === 'quantity') ? sanitizeNumber(value) : value };
            return { ...prev, items_update: items };
        });
    };

    const validate = () => {
        const newErrors = {};
        // Never submit an advice with no items (happens if the detail fetch failed and
        // left the form blank). Blocks the zero-item submit.
        if (!Array.isArray(formData.items_update) || formData.items_update.length === 0) {
            newErrors._items = formStrings.errors?.noItems || 'No items available to advise.';
        }
        if (!formData.advice_date) newErrors.advice_date = formStrings.errors.adviceDateRequired;

        // Validate vehicle number
        if (!formData.vehicle_number || String(formData.vehicle_number).trim() === '') {
            newErrors.vehicle_number = formStrings.errors.vehicleNumberRequired;
        } else if (!validateUtils.isValidVehicleNumber(formData.vehicle_number)) {
            newErrors.vehicle_number = commonStrings.form.startTruck.messages.invalidVehicleNumber;
        }

        // Validate supplier invoice number
        if (!formData.supplier_invoice_number || String(formData.supplier_invoice_number).trim() === '') {
            newErrors.supplier_invoice_number = formStrings.errors.supplierInvoiceNumberRequired;
        }

        // Validate items
        formData.items_update.forEach((item, index) => {
            const qty = parseFloat(item.quantity);
            if (item.quantity === '' || item.quantity === null || item.quantity === undefined || isNaN(qty) || qty < 0) {
                newErrors[`items_update.${index}.quantity`] = formStrings.errors.invalidQuantity;
            } else if (qty > parseFloat(item.ordered_quantity)) {
                newErrors[`items_update.${index}.quantity`] = formStrings.errors.quantityExceeds;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = {
                advice_date: formData.advice_date,
                payment_terms: formData.payment_terms || '',
                vehicle_number: formData.vehicle_number || '',
                supplier_invoice_number: formData.supplier_invoice_number || '',
                supplier_invoice_date: formData.supplier_invoice_date || null,
                remark: formData.remark || '',
                items: formData.items_update.map(item => ({
                    purchase_order_item_id: item.id,
                    quantity: parseFloat(item.quantity) || 0,
                    remark: item.remark || '',
                })),
            };
            // Close ONLY on success — a backend rejection (qty exceeds pending, 409,
            // 422) previously still closed the form and discarded the entered advice
            // quantities / vehicle no. / supplier invoice. The parent shows the error.
            const result = await onSubmit(payload);
            if (!result || result.success) onClose();
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {(submitting || loading) && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.6) }}></div>
                    </div>
                )}

                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formStrings.title}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-6">
                    {fetchError && (
                        // The PO detail fetch failed/404'd (e.g. deleted in another tab).
                        // Tell the operator instead of showing a blank, submittable form.
                        <div role="alert" className="mb-4 rounded-lg px-4 py-3 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08), color: UI_COLORS.STATUS.ERROR, border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}` }}>
                            {t('purchaseOrdersModule.common.loadFailed', 'Could not load this purchase order — it may have been deleted, or the server is unavailable. Close and try again.')}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.fields.adviceDate.label} <span>*</span></label>
                                <input type="date" name="advice_date" value={formData.advice_date} onChange={handleChange} min={today} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.advice_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                {errors.advice_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.advice_date}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.fields.paymentTerms.label}</label>
                                <input name="payment_terms" value={formData.payment_terms} onChange={handleChange} placeholder={formStrings.fields.paymentTerms.placeholder} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: UI_COLORS.FORMS.INPUT.border }} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                    {formStrings.fields.vehicleNumber.label} <span>*</span>
                                    <span
                                        className="ml-2 cursor-pointer relative inline-block"
                                        onMouseEnter={() => setShowVehicleTooltip(true)}
                                        onMouseLeave={() => setShowVehicleTooltip(false)}
                                        style={{ color: UI_COLORS.TEXT.MUTED }}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                        {showVehicleTooltip && (
                                            <span
                                                className="absolute left-0 top-6 z-50 px-3 py-2 text-xs font-normal rounded-lg shadow-lg"
                                                style={{
                                                    backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                                                    color: UI_COLORS.TEXT.PRIMARY,
                                                    border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                                                    minWidth: '200px',
                                                }}
                                            >
                                                <div>{formStrings.fields.vehicleNumber.hint}</div>
                                            </span>
                                        )}
                                    </span>
                                </label>
                                <input
                                    name="vehicle_number"
                                    value={formData.vehicle_number}
                                    onChange={handleChange}
                                    placeholder={formStrings.fields.vehicleNumber.placeholder}
                                    className="w-full px-4 py-3 rounded-xl border-2"
                                    style={{ borderColor: errors.vehicle_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                />
                                {errors.vehicle_number && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.vehicle_number}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.fields.supplierInvoiceNumber.label} <span>*</span></label>
                                <input 
                                    name="supplier_invoice_number" 
                                    value={formData.supplier_invoice_number} 
                                    onChange={handleChange} 
                                    placeholder={formStrings.fields.supplierInvoiceNumber.placeholder} 
                                    className="w-full px-4 py-3 rounded-xl border-2" 
                                    style={{ borderColor: errors.supplier_invoice_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} 
                                />
                                {errors.supplier_invoice_number && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.supplier_invoice_number}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.fields.supplierInvoiceDate.label}</label>
                                <input type="date" name="supplier_invoice_date" value={formData.supplier_invoice_date} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: UI_COLORS.FORMS.INPUT.border }} />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.fields.remark.label}</label>
                            <textarea
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                rows="2"
                                className="w-full px-4 py-3 rounded-xl border-2 transition-all duration-200"
                                style={{ 
                                    borderColor: formData.remark ? '#fde047' : UI_COLORS.FORMS.INPUT.border,
                                    backgroundColor: formData.remark ? '#fefce8' : UI_COLORS.FORMS.INPUT.background,
                                    color: UI_COLORS.TEXT.PRIMARY
                                }}
                                placeholder={formStrings.fields.remark.placeholder}
                            />
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formStrings.headings.items}</h3>
                            <div className="border rounded-xl overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="px-4 py-3 font-medium text-gray-700">{formStrings.table.columns.product}</th>
                                            <th className="px-4 py-3 font-medium text-gray-700">{formStrings.table.columns.orderedQty}</th>
                                            <th className="px-4 py-3 font-medium text-gray-700">{formStrings.table.columns.pendingQty}</th>
                                            <th className="px-4 py-3 font-medium text-gray-700">{formStrings.table.columns.adviceQty}</th>
                                            <th className="px-4 py-3 font-medium text-gray-700">{formStrings.table.columns.remainingQty}</th>
                                            <th className="px-4 py-3 font-medium text-gray-700">Remark</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {formData.items_update.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                                                    No items available
                                                </td>
                                            </tr>
                                        ) : (
                                            formData.items_update.map((item, idx) => {
                                                const pendingQty = parseFloat(item.pending_quantity) || 0;
                                                const adviceQty = parseFloat(item.quantity) || 0;
                                                const remainingQty = pendingQty - adviceQty;
                                                return (
                                                    <tr key={item.id}>
                                                        <td className="px-4 py-3 font-medium text-gray-900">{item.product_name}</td>
                                                        <td className="px-4 py-3 text-gray-500">{item.ordered_quantity} {item.unit}</td>
                                                        <td className="px-4 py-3 text-gray-500">{item.pending_quantity} {item.unit}</td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                                                                className="w-24 px-2 py-1 rounded border-2 text-right"
                                                                style={{ borderColor: errors[`items_update.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                                            />
                                                            {errors[`items_update.${idx}.quantity`] && <p className="text-xs mt-1 text-red-500">{errors[`items_update.${idx}.quantity`]}</p>}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-500">
                                                            <span className={remainingQty < 0 ? 'text-red-600 font-semibold' : ''}>
                                                                {remainingQty.toFixed(2)} {item.unit}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <textarea
                                                                value={item.remark || ''}
                                                                onChange={(e) => handleItemChange(idx, 'remark', e.target.value)}
                                                                rows="3"
                                                                className="w-full px-2 py-1 rounded border-2 transition-all duration-200 text-sm font-medium resize-none overflow-y-auto"
                                                                style={{
                                                                    backgroundColor: item.remark ? '#fefce8' : UI_COLORS.FORMS.INPUT.background,
                                                                    borderColor: item.remark ? '#fde047' : UI_COLORS.FORMS.INPUT.border,
                                                                    color: item.remark ? '#854d0e' : UI_COLORS.TEXT.PRIMARY,
                                                                    whiteSpace: 'pre-wrap'
                                                                }}
                                                                placeholder="Note..."
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{commonStrings.buttons.cancel}</button>
                            <button type="submit" className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                                <FontAwesomeIcon icon={faSave} className="mr-2" />
                                {formStrings.buttons.generate}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderAdviceForm;
